import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import api from '../services/api';

const SiteContext = createContext();

export const useSite = () => {
  const context = useContext(SiteContext);
  if (!context) {
    throw new Error('useSite must be used within a SiteProvider');
  }
  return context;
};

export const SiteProvider = ({ children }) => {
  const { user } = useAuth();
  const [availableSites, setAvailableSites] = useState([]);
  const [selectedSiteId, setSelectedSiteId] = useState(null);
  const [selectedSiteName, setSelectedSiteName] = useState('');
  const [loading, setLoading] = useState(true);

  // Charger les sites disponibles pour l'utilisateur
  const loadSites = useCallback(async () => {
    if (!user) {
      setAvailableSites([]);
      setSelectedSiteId(null);
      setSelectedSiteName('');
      setLoading(false);
      return;
    }

    try {
      // Source UNIQUE : sites du client connecté (depuis ses licences).
      // Pas de fallback inter-clients : si l'utilisateur n'a aucune licence active,
      // la liste reste vide.
      let clientSites = [];
      try {
        clientSites = await api.getMySites();
      } catch (e) {
        console.warn('getMySites failed:', e);
        clientSites = [];
      }

      // Normaliser chaque site
      clientSites = (clientSites || []).map(s => ({
        ...s,
        site_id: s.site_id || s.id || '',
        name: s.name || `Site ${(s.site_id || s.id || '').charAt(0).toUpperCase() + (s.site_id || s.id || '').slice(1)}`,
      }));

      setAvailableSites(clientSites);

      // Déterminer le site sélectionné par défaut
      if (user.site_scope === 'single') {
        const userSite = clientSites.find(s => s.site_id === user.site_id);
        setSelectedSiteId(user.site_id);
        setSelectedSiteName(userSite?.name || user.site_id || '');
      } else if (user.site_scope === 'multi') {
        const firstSite = clientSites.find(s => (user.site_ids || []).includes(s.site_id));
        if (firstSite) {
          setSelectedSiteId(firstSite.site_id);
          setSelectedSiteName(firstSite.name);
        }
      } else if (user.role === 'super_admin' && clientSites.length > 0) {
        // Super Admin : présélectionner le premier site (il gère tous les clients)
        setSelectedSiteId(clientSites[0].site_id);
        setSelectedSiteName(clientSites[0].name);
      } else {
        setSelectedSiteId(null);
        setSelectedSiteName('Tous les sites');
      }
    } catch (err) {
      console.error('Erreur chargement sites:', err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadSites();
  }, [loadSites]);

  // Changer le site sélectionné.
  // `fallbackName` permet à un caller externe (ex: dashboard multi-sites) de
  // forcer un nom propre même si le site n'est pas (encore) dans availableSites.
  const selectSite = (siteId, fallbackName = null) => {
    if (siteId === '' || siteId === null) {
      setSelectedSiteId(null);
      // Label différent selon le scope : un user multi voit son périmètre,
      // pas tout le client.
      const isMulti = user?.site_scope === 'multi';
      setSelectedSiteName(isMulti ? 'Tous mes sites' : 'Tous les sites');
    } else {
      const site = availableSites.find(s => s.site_id === siteId || s.id === siteId);
      setSelectedSiteId(siteId);
      setSelectedSiteName(site?.name || fallbackName || siteId);
    }
  };

  // Est-ce que l'utilisateur peut changer de site ?
  const canChangeSite = user?.site_scope === 'all' || user?.site_scope === 'multi' || user?.role === 'direction_centrale';

  // Est-ce qu'on affiche une option "tout le périmètre" en haut du dropdown ?
  // - all/direction_centrale → "Tous les sites" (du client)
  // - multi → "Tous mes sites" (uniquement les sites supervisés)
  const canSelectAll = user?.site_scope === 'all' || user?.site_scope === 'multi' || user?.role === 'direction_centrale';
  const allOptionLabel = user?.site_scope === 'multi' ? 'Tous mes sites' : 'Tous les sites';

  return (
    <SiteContext.Provider value={{
      availableSites,
      selectedSiteId,
      selectedSiteName,
      selectSite,
      canChangeSite,
      canSelectAll,
      allOptionLabel,
      loading,
      reloadSites: loadSites
    }}>
      {children}
    </SiteContext.Provider>
  );
};

export default SiteContext;
